import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { crvProductService, craProductService, phProductService } from '../services/productService';
import { remittanceService } from '../services/remittanceService';
import { CrvProduct, CraProduct, PhProduct, ProductType, PRODUCT_TYPE_LABELS } from '../types/InvestmentProduct';
import CrvForm from '../components/products/CrvForm';
import CraForm from '../components/products/CraForm';
import PhForm from '../components/products/PhForm';
import ConfirmModal from '../components/common/ConfirmModal';
import ActionButton from '../components/common/ActionButton';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { FaPlus, FaEdit, FaTrash, FaFileExport } from 'react-icons/fa';
import { BsBriefcase } from 'react-icons/bs';
import './Products.css';

const Products: React.FC = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<ProductType>('crv');
  const [crvProducts, setCrvProducts] = useState<CrvProduct[]>([]);
  const [craProducts, setCraProducts] = useState<CraProduct[]>([]);
  const [phProducts, setPhProducts] = useState<PhProduct[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{ id: string; name: string } | null>(null);

  // Remesa
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [remittanceQuarter, setRemittanceQuarter] = useState<number>(Math.ceil((new Date().getMonth() + 1) / 3));
  const [remittanceYear, setRemittanceYear] = useState<number>(new Date().getFullYear());
  const [generatingRemittance, setGeneratingRemittance] = useState(false);

  // Filtros
  const [filterNombre, setFilterNombre] = useState('');
  const [filterCif, setFilterCif] = useState('');
  const [filterRazonSocial, setFilterRazonSocial] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setSelectedProductIds([]);
    try {
      if (activeTab === 'crv') {
        const response = await crvProductService.getAll();
        setCrvProducts(response.data);
      } else if (activeTab === 'cra') {
        const response = await craProductService.getAll();
        setCraProducts(response.data);
      } else {
        const response = await phProductService.getAll();
        setPhProducts(response.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleEdit = (product: any) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDeleteClick = (id: string, name: string) => {
    setProductToDelete({ id, name });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    try {
      if (activeTab === 'crv') {
        await crvProductService.delete(productToDelete.id);
      } else if (activeTab === 'cra') {
        await craProductService.delete(productToDelete.id);
      } else {
        await phProductService.delete(productToDelete.id);
      }
      showToast('Producto eliminado correctamente');
      loadData();
      setShowDeleteModal(false);
      setProductToDelete(null);
    } catch (error) {
      console.error('Error deleting product:', error);
      showToast('Error al eliminar el producto', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  const handleSelectProduct = (id: string) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (data: any[]) => {
    const allIds = data.map((p) => p.id!);
    const allSelected = allIds.every((id) => selectedProductIds.includes(id));
    if (allSelected) {
      setSelectedProductIds([]);
    } else {
      setSelectedProductIds(allIds);
    }
  };

  const handleGenerateRemittance = async () => {
    setGeneratingRemittance(true);
    try {
      const response = await remittanceService.generate(selectedProductIds, remittanceQuarter, remittanceYear);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `remesa_${remittanceQuarter}T${remittanceYear}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Remesa generada correctamente');
    } catch (error) {
      showToast('Error al generar la remesa', 'error');
    } finally {
      setGeneratingRemittance(false);
    }
  };

  const clearFilters = () => {
    setFilterNombre('');
    setFilterCif('');
    setFilterRazonSocial('');
  };

  const filterData = (data: any[]) => {
    return data.filter((product) => {
      const matchesNombre = product.nombreProducto.toLowerCase().includes(filterNombre.toLowerCase());
      const matchesCif = product.cifNif.toLowerCase().includes(filterCif.toLowerCase());
      const matchesRazonSocial = product.razonSocial.toLowerCase().includes(filterRazonSocial.toLowerCase());
      return matchesNombre && matchesCif && matchesRazonSocial;
    });
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingProduct(null);
    loadData();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const renderTable = () => {
    let data: any[] = [];
    let columns: { label: string; align?: string }[] = [];

    if (activeTab === 'crv') {
      data = filterData(crvProducts);
      columns = [
        { label: 'ID' }, { label: 'Nombre' }, { label: 'Razón Social' }, { label: 'CIF/NIF' },
        { label: 'Cap. Objetivo', align: 'right' }, { label: 'Cap. Captado', align: 'right' }, { label: 'Estado' },
      ];
    } else if (activeTab === 'cra') {
      data = filterData(craProducts);
      columns = [
        { label: 'ID' }, { label: 'Nombre' }, { label: 'Razón Social' }, { label: 'CIF/NIF' },
        { label: 'Tickets Obj.', align: 'right' }, { label: 'Cap. Captado', align: 'right' }, { label: 'Estado' },
      ];
    } else {
      data = filterData(phProducts);
      columns = [
        { label: 'ID' }, { label: 'Nombre' }, { label: 'Razón Social' }, { label: 'CIF/NIF' },
        { label: 'Cap. Objetivo', align: 'right' }, { label: 'Cap. Captado', align: 'right' }, { label: 'Estado' },
      ];
    }

    if (loading) {
      return <LoadingSpinner />;
    }

    if (data.length === 0) {
      return <EmptyState icon={BsBriefcase} title="No hay productos registrados" subtitle="Añade el primero con el botón + Nuevo producto" />;
    }

    const allIds = data.map((p) => p.id!);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedProductIds.includes(id));

    return (
      <table className="products-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => handleSelectAll(data)}
              />
            </th>
            {columns.map((col) => (
              <th key={col.label} className={col.align === 'right' ? 'td-right' : undefined}>{col.label}</th>
            ))}
            <th className="col-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {data.map((product) => (
            <tr key={product.id}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedProductIds.includes(product.id!)}
                  onChange={() => handleSelectProduct(product.id!)}
                />
              </td>
              <td className="id-cell">{product.id}</td>
              <td>{product.nombreProducto}</td>
              <td>{product.razonSocial}</td>
              <td>{product.cifNif}</td>
              {activeTab === 'crv' && (
                <>
                  <td className="td-right">{formatCurrency((product as CrvProduct).capObjetivoGlobal)}</td>
                  <td className="td-right">{formatCurrency((product as CrvProduct).capitalCaptado)}</td>
                  <td>
                    <span className={`status-badge status-${(product as CrvProduct).estadoGlobal?.toLowerCase().replace(/\s+/g, '-')}`}>
                      {(product as CrvProduct).estadoGlobal || '-'}
                    </span>
                  </td>
                </>
              )}
              {activeTab === 'cra' && (
                <>
                  <td className="td-right">{(product as CraProduct).ticketsObjetivo || '-'}</td>
                  <td className="td-right">{formatCurrency((product as CraProduct).capitalCaptado)}</td>
                  <td>
                    <span className={`status-badge status-${(product as CraProduct).estadoSociedad?.toLowerCase().replace(/\s+/g, '-')}`}>
                      {(product as CraProduct).estadoSociedad || '-'}
                    </span>
                  </td>
                </>
              )}
              {activeTab === 'ph' && (
                <>
                  <td className="td-right">{formatCurrency((product as PhProduct).capObjetivoGlobal)}</td>
                  <td className="td-right">{formatCurrency((product as PhProduct).capitalCaptado)}</td>
                  <td>
                    <span className={`status-badge status-${(product as PhProduct).estadoGlobal?.toLowerCase().replace(/\s+/g, '-')}`}>
                      {(product as PhProduct).estadoGlobal || '-'}
                    </span>
                  </td>
                </>
              )}
              <td className="actions">
                <ActionButton
                  icon={FaEdit}
                  text="Editar Producto"
                  onClick={() => handleEdit(product)}
                  variant="edit"
                />
                <ActionButton
                  icon={FaTrash}
                  text="Eliminar Producto"
                  onClick={() => handleDeleteClick(product.id!, product.nombreProducto)}
                  variant="delete"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="products-page">
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar eliminación"
        message={`¿Estás seguro de que deseas eliminar ${productToDelete?.name}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'crv' ? 'active' : ''}`}
          onClick={() => setActiveTab('crv')}
        >
          {PRODUCT_TYPE_LABELS.crv}
        </button>
        <button
          className={`tab ${activeTab === 'cra' ? 'active' : ''}`}
          onClick={() => setActiveTab('cra')}
        >
          {PRODUCT_TYPE_LABELS.cra}
        </button>
        <button
          className={`tab ${activeTab === 'ph' ? 'active' : ''}`}
          onClick={() => setActiveTab('ph')}
        >
          {PRODUCT_TYPE_LABELS.ph}
        </button>
        <div className="tab-actions">
          <ActionButton
            icon={FaPlus}
            text="Crear Producto"
            onClick={handleCreate}
            variant="primary"
          />
        </div>
      </div>

      {!showForm && (
        <div className="filters-container">
          <div className="filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={filterNombre}
              onChange={(e) => setFilterNombre(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <input
              type="text"
              placeholder="Buscar por CIF/NIF..."
              value={filterCif}
              onChange={(e) => setFilterCif(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <input
              type="text"
              placeholder="Buscar por razón social..."
              value={filterRazonSocial}
              onChange={(e) => setFilterRazonSocial(e.target.value)}
              className="filter-input"
            />
          </div>
          {(filterNombre || filterCif || filterRazonSocial) && (
            <button onClick={clearFilters} className="btn-clear-filters">
              Limpiar filtros
            </button>
          )}
        </div>
        </div>
      )}

      {!showForm && (
        <div className="remittance-panel">
          <div className="remittance-controls">
            <span className="selected-count">
              {selectedProductIds.length} producto{selectedProductIds.length !== 1 ? 's' : ''} seleccionado{selectedProductIds.length !== 1 ? 's' : ''}
            </span>
            <select value={remittanceQuarter} onChange={(e) => setRemittanceQuarter(Number(e.target.value))}>
              <option value={1}>1T</option>
              <option value={2}>2T</option>
              <option value={3}>3T</option>
              <option value={4}>4T</option>
            </select>
            <select value={remittanceYear} onChange={(e) => setRemittanceYear(Number(e.target.value))}>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              className="btn-icon-only"
              disabled={selectedProductIds.length === 0 || generatingRemittance}
              onClick={handleGenerateRemittance}
              title="Generar remesa"
              aria-label="Generar remesa"
            >
              {React.createElement(FaFileExport as React.ComponentType)}
            </button>
          </div>
        </div>
      )}

      <div className="products-content">
        {!showForm ? (
          renderTable()
        ) : (
          <div className="form-container">
            {activeTab === 'crv' && (
              <CrvForm
                product={editingProduct}
                onSuccess={handleFormSuccess}
                onCancel={handleFormCancel}
              />
            )}
            {activeTab === 'cra' && (
              <CraForm
                product={editingProduct}
                onSuccess={handleFormSuccess}
                onCancel={handleFormCancel}
              />
            )}
            {activeTab === 'ph' && (
              <PhForm
                product={editingProduct}
                onSuccess={handleFormSuccess}
                onCancel={handleFormCancel}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
